// @noflow
import { useLocalisation } from '@/context/localisation'
import { Meal, ProductBenefit } from '@/gql/graphql'
import classNames from 'classnames'
import camelCase from 'lodash/camelCase'
import isNull from 'lodash/isNull'
import isUndefined from 'lodash/isUndefined'
import React, { useCallback, useContext, useState } from 'react'

import { useModal } from '@/hooks/useModal'

// Components
import Card from '@/components/elements/atoms/Card/Card'
import { cloudinaryPath } from '@/components/elements/atoms/CloudinaryImage/CloudinaryImage'
import Notification from '@/components/elements/atoms/InPageNotification/InPageNotification'
import Interactive from '@/components/elements/atoms/Interactive/Interactive'
import QuantitySelector from '@/components/elements/atoms/QuantitySelector/QuantitySelector'
import Text from '@/components/elements/atoms/Text/Text'
import { EditFreshMealsContext } from '@/components/pages/EditRecipes/EditRecipes'
import type { MealInputKey } from '@/components/pages/EditRecipes/EditRecipes'
import STYLES from '@/components/pages/EditRecipes/EditRecipes.module.sass'

const RecipeCard = ({
  identifier = '',
  freshMeal,
  showBenefits,
  topPick = false,
  hasAllergies = false,
  dogName,
  dogCount
}: {
  freshMeal: Meal
  showBenefits: boolean
  topPick?: boolean
  hasAllergies?: boolean
  dogName?: string
  dogCount?: number
  identifier?: string
}): JSX.Element => {
  const { formatCurrencyWithDecimal } = useLocalisation()
  const { openModal } = useModal('recipe-detail-modal')
  const [toggleHoverImage, setToggleHoverImage] = useState(false)

  const {
    mealQuantities,
    increaseMealQuantity,
    decreaseMealQuantity,
    pouchesPerServing
  } = useContext(EditFreshMealsContext)

  const {
    flavour: {
      slug,
      recipeSurcharge,
      name,
      thumbnail: { src: thumbnailSrc },
      primaryImage: { src: primaryImageSrc },
      productBenefits
    }
  } = freshMeal
  const mealInputKey = camelCase(slug) as MealInputKey

  const formattedRecipeSurcharge = recipeSurcharge
    ? formatCurrencyWithDecimal(recipeSurcharge)
    : ''

  const flavourQuantity = mealQuantities[mealInputKey as MealInputKey] ?? 0

  const onQuantityChangeCallback = useCallback(
    (to: number) => {
      const incrementAmount = pouchesPerServing === 2 ? 2 : 1
      Number(to) > flavourQuantity
        ? increaseMealQuantity(mealInputKey, incrementAmount)
        : decreaseMealQuantity(mealInputKey, incrementAmount)
    },
    [
      flavourQuantity,
      increaseMealQuantity,
      mealInputKey,
      decreaseMealQuantity,
      pouchesPerServing
    ]
  )

  const handleRecipeClick = useCallback(() => {
    openModal({ product: freshMeal })
  }, [freshMeal, openModal])

  const recipeImgClass = classNames(STYLES.recipeImg, {
    [STYLES.recipeImgTopPick]: topPick,
    [STYLES.recipeImgAllergen]: hasAllergies
  })

  const cardContentClass = classNames(STYLES.cardContent, {
    [STYLES.cardContentTopPick]: topPick
  })

  const handleToggleHoverImage = useCallback(() => {
    setToggleHoverImage(!toggleHoverImage)
  }, [toggleHoverImage])

  return (
    <Card
      padding={0}
      fill={false}
      className={`${topPick ? '' : STYLES.cardShadow}`}
      variant={topPick ? 'brandYellow100' : undefined}
      mask
    >
      {showBenefits && (
        <div className={STYLES.benefitList}>
          {productBenefits.map((benefit: ProductBenefit) => (
            <Text
              key={benefit.name}
              text={benefit.name}
              translate={false}
              margin={false}
              variant="textRegular14"
              bold
            />
          ))}
        </div>
      )}
      <div className={cardContentClass}>
        <Interactive
          className={STYLES.recipeCardButton}
          element="button"
          onInteraction={handleRecipeClick}
          onMouseEnter={handleToggleHoverImage}
          onMouseLeave={handleToggleHoverImage}
        >
          <div className={recipeImgClass}>
            <img
              src={cloudinaryPath({
                path: toggleHoverImage ? primaryImageSrc : thumbnailSrc,
                transformations: {
                  width: 164,
                  height: 152,
                  crop: 'fill',
                  dpr: 2
                }
              })}
              alt={mealInputKey}
              className={STYLES.image}
            />
          </div>
        </Interactive>
        <div className={STYLES.recipeDetails}>
          <div className={STYLES.heading}>
            <Text
              text={name}
              translate={false}
              margin={false}
              variant="display16"
            />
            {!isNull(recipeSurcharge) &&
              !isUndefined(recipeSurcharge) &&
              recipeSurcharge > 0 && (
                <div>
                  <Text
                    text={'edit_recipes.recipe_surcharge'}
                    namespace="account"
                    variables={{ price: formattedRecipeSurcharge }}
                    margin={false}
                  />
                </div>
              )}
          </div>
          <QuantitySelector
            controlled
            minValue={0}
            maxValue={1000} // An overly large number to ensure the quantity selector is not limited by the max value
            onChange={onQuantityChangeCallback}
            quantity={flavourQuantity}
            variant="yellow"
            size={30}
            identifier={[identifier, 'recipe_card_quantity'].join('.')}
            trackDisabledPresses
          />
        </div>
      </div>
      {hasAllergies && (
        <div className={STYLES.allergyMsg}>
          <Notification
            text={{
              text: 'edit_recipes.fresh_meals_page.recommended_recipes.allergen_notification',
              namespace: 'account',
              variables: {
                dogName: dogName,
                count: dogCount
              }
            }}
            variant="warn"
          />
        </div>
      )}
    </Card>
  )
}

export { RecipeCard }
